// @flow
import * as React from 'react'
import type { Municipality } from 'sources/types'
import * as topojson from 'topojson-client'
import config from 'config'
import type { GeojsonFeature } from 'components/map/component'

export type D3Map<T> = {
  get: string => T,
  size: () => number,
  empty: () => boolean
}

type MapProps = {
  currentBudget: number,
  gemeinden: D3Map<Municipality>,
  geometryCollection: { type: string, geometries: Array<GeojsonFeature> },
  mapId: string,
  pathGenerator: GeojsonFeature => string
}

const kreisFeatures: Array<GeojsonFeature> = topojson.feature(
  config.schweiz,
  config.zuercherKreise
).features

/**
 * Draw a choropleth map
 * @param {object} props
 * @param props.gemeinden
 * @constructor
 */
export function ChoroplethMap ({
  currentBudget,
  gemeinden,
  geometryCollection,
  mapId = '',
  pathGenerator
}: MapProps): React.Element<'g'> | null {
  if (!gemeinden || gemeinden.size() <= 0) {
    return null
  }

  // Get an indexed version of the municipalities, so we can find the values
  // for each
  const indexedMunicipalities = gemeinden

  const gemeindeFeatures: Array<GeojsonFeature> = topojson.feature(
    config.schweiz,
    geometryCollection
  ).features

  /**
   * Draw the individual municipalities with the correct color fill
   * @param colorGenerator
   * @param areas
   * @param areaIdProperty
   * @param idGenerator
   * @returns {any[]}
   */
  function drawSwissMunicipalities (
    colorGenerator: number => string,
    areas: Array<GeojsonFeature>,
    areaIdProperty = 'GMDNR',
    idGenerator = property => property
  ): Array<React.Element<'path' | 'g'>> {
    return areas.map((feature: GeojsonFeature, index: number) => {
      const municipalityId = idGenerator(+feature.properties[areaIdProperty])

      const municipalityData = indexedMunicipalities.get(
        municipalityId.toString()
      )
      let generatedColor: string = colorGenerator(
        +municipalityData.summedPriceRangeQuotas[currentBudget - 1]
      )

      if (municipalityData.sizeRangeQuota <= 0) {
        generatedColor = config.invalidColor
      }

      let colouredMunicipalityPath = (
        <path
          key={municipalityId}
          d={pathGenerator(feature)}
          strokeWidth={0}
          vectorEffect='non-scaling-stroke'
          stroke={generatedColor}
          fill={generatedColor}
          id={`${mapId}-gemeinde-${municipalityId}`}
          className='gemeinde'
        />
      )

      return colouredMunicipalityPath
    })
  }

  function drawCrosshatch (municipalities: Array<GeojsonFeature>) {
    let lowOfferMunicipalities = municipalities.filter(municipality => {
      const municipalityData = indexedMunicipalities.get(
        municipality.properties['GMDNR'].toString()
      )
      return municipalityData.angebot_gem < config.notEnoughData
    })

    const mergedFeature = topojson.merge(config.schweiz, lowOfferMunicipalities)

    return (
      <path
        className='low-offer-municipalities'
        d={pathGenerator(mergedFeature)}
        fill={`url(#${mapId}-diagonalHatch)`}
        style={{ pointerEvents: 'none' }}
      />
    )
  }

  const municipalityPaths = drawSwissMunicipalities(
    config.colorGenerator,
    gemeindeFeatures
  )
  const zuerichKreise = drawSwissMunicipalities(
    config.colorGenerator,
    kreisFeatures,
    'knr',
    id => `261${id.toString().padStart(2, '0')}`
  )

  const crosshatch = drawCrosshatch(geometryCollection.geometries)
  const partsPaths = municipalityPaths.concat(zuerichKreise)

  return (
    <g className='all municipalities'>
      <g className='percentage-of-offers'>{partsPaths}</g>
      {crosshatch}
    </g>
  )
}
